import { Injectable } from '@angular/core';
import * as signalR from '@aspnet/signalr';

@Injectable()
export class SignalRService {
  private hubConnection: signalR.HubConnection | null = null;
  private connectionUrl = '/signalRHub';
  private currentProjectId: any = null;
  private currentUserName: string | null = null;
  private onReadyCallback: (() => void) | null = null;
  private intentionalStop = false;
  private reconnectTimer: any = null;

  private receiveEditingCb: ((rowId: string, userName: string) => void) | null = null;
  private userStoppedEditingCb: ((rowId: string, userName: string) => void) | null = null;
  private lockedCb: ((userName: string) => void) | null = null;
  private unlockedCb: ((userName: string) => void) | null = null;
  private presenceSnapshotCb: ((users: string[]) => void) | null = null;
  private userJoinedCb: ((userName: string) => void) | null = null;
  private userLeftCb: ((userName: string) => void) | null = null;
  private fieldChangedCb: ((cellKey: string, userName: string) => void) | null = null;
  private fieldChangeRevertedCb: ((cellKey: string, userName: string) => void) | null = null;
  private fieldChangesClearedCb: ((userName: string) => void) | null = null;
  private fieldChangesSnapshotCb: ((cellKeys: string[], userNames: string[]) => void) | null = null;

  constructor() { }

  public connect(projectId: any, onReady?: () => void): void {
    // Tear down any previous connection first so presence leaves the old project.
    this.disconnect(this.currentUserName || undefined).then(() => {
      this.intentionalStop = false;
      this.currentProjectId = projectId;
      this.onReadyCallback = onReady || null;

      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.connectionUrl}?projectId=${projectId}`)
        .build();

      this.registerLifecycleHandlers(projectId);
      this.bindAllEvents();
      this.startConnection();
    });
  }

  /**
   * Leave presence (if possible) and fully stop the hub connection.
   * Prevents stale "Also here" users after navigating away.
   */
  public async disconnect(userName?: string): Promise<void> {
    this.intentionalStop = true;
    this.clearReconnectTimer();

    const connection = this.hubConnection;
    const projectId = this.currentProjectId;
    const name = userName || this.currentUserName;
    this.hubConnection = null;

    if (!connection) {
      return;
    }

    try {
      if (connection.state === signalR.HubConnectionState.Connected && projectId && name) {
        try {
          await connection.invoke('LeavePresence', String(projectId), name);
        } catch (err) {
          console.error('LeavePresence error during disconnect:', err);
        }
      }
      await connection.stop();
    } catch (err) {
      console.error('SignalR disconnect error:', err);
    }
  }

  private async startConnection(): Promise<void> {
    if (!this.hubConnection || this.intentionalStop) {
      return;
    }

    try {
      await this.hubConnection.start();
      console.log('SignalR connected.');
      if (this.onReadyCallback) {
        this.onReadyCallback();
      }
    } catch (err) {
      console.error('SignalR connection error:', err);
      if (!this.intentionalStop) {
        this.reconnectTimer = setTimeout(() => this.startConnection(), 5000);
      }
    }
  }

  private registerLifecycleHandlers(projectId: any): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection.onclose(() => {
      console.warn('SignalR disconnected');
      if (this.intentionalStop) {
        return;
      }
      this.clearReconnectTimer();
      this.reconnectTimer = setTimeout(() => this.reconnect(projectId), 1000);
    });
  }

  private async reconnect(projectId: any): Promise<void> {
    if (this.intentionalStop) {
      return;
    }

    try {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.connectionUrl}?projectId=${projectId}`)
        .build();

      this.registerLifecycleHandlers(projectId);
      this.bindAllEvents();
      await this.startConnection();
    } catch (err) {
      console.error('Reconnect failed:', err);
      if (!this.intentionalStop) {
        this.reconnectTimer = setTimeout(() => this.reconnect(projectId), 10000);
      }
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private bindAllEvents(): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection.off('ReceiveEditing');
    this.hubConnection.on('ReceiveEditing', (rowId: string, userName: string) => {
      if (this.receiveEditingCb) {
        this.receiveEditingCb(rowId, userName);
      }
    });

    this.hubConnection.off('UserStoppedEditing');
    this.hubConnection.on('UserStoppedEditing', (rowId: string, userName: string) => {
      if (this.userStoppedEditingCb) {
        this.userStoppedEditingCb(rowId, userName);
      }
    });

    this.hubConnection.off('ReceiveLocked');
    this.hubConnection.on('ReceiveLocked', (userName: string) => {
      if (this.lockedCb) {
        this.lockedCb(userName);
      }
    });

    this.hubConnection.off('ReceiveUnlocked');
    this.hubConnection.on('ReceiveUnlocked', (userName: string) => {
      if (this.unlockedCb) {
        this.unlockedCb(userName);
      }
    });

    this.hubConnection.off('PresenceSnapshot');
    this.hubConnection.on('PresenceSnapshot', (users: string[]) => {
      if (this.presenceSnapshotCb) {
        this.presenceSnapshotCb(users || []);
      }
    });

    this.hubConnection.off('UserJoined');
    this.hubConnection.on('UserJoined', (userName: string) => {
      if (this.userJoinedCb) {
        this.userJoinedCb(userName);
      }
    });

    this.hubConnection.off('UserLeft');
    this.hubConnection.on('UserLeft', (userName: string) => {
      if (this.userLeftCb) {
        this.userLeftCb(userName);
      }
    });

    this.hubConnection.off('ReceiveFieldChanged');
    this.hubConnection.on('ReceiveFieldChanged', (cellKey: string, userName: string) => {
      if (this.fieldChangedCb) {
        this.fieldChangedCb(cellKey, userName);
      }
    });

    this.hubConnection.off('ReceiveFieldChangeReverted');
    this.hubConnection.on('ReceiveFieldChangeReverted', (cellKey: string, userName: string) => {
      if (this.fieldChangeRevertedCb) {
        this.fieldChangeRevertedCb(cellKey, userName);
      }
    });

    this.hubConnection.off('ReceiveFieldChangesCleared');
    this.hubConnection.on('ReceiveFieldChangesCleared', (userName: string) => {
      if (this.fieldChangesClearedCb) {
        this.fieldChangesClearedCb(userName);
      }
    });

    this.hubConnection.off('FieldChangesSnapshot');
    this.hubConnection.on('FieldChangesSnapshot', (cellKeys: string[], userNames: string[]) => {
      if (this.fieldChangesSnapshotCb) {
        this.fieldChangesSnapshotCb(cellKeys || [], userNames || []);
      }
    });
  }

  public notifyEditing(projectId: any, rowId: string, userName: any): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke('NotifyEditing', projectId, rowId, userName)
        .catch(err => console.error('NotifyEditing error:', err));
    } else {
      console.warn('Hub not connected, cannot send NotifyEditing');
    }
  }

  public notifyStoppedEditing(projectId: any, rowId: string, userName: any): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke('StopEditing', projectId, rowId, userName)
        .catch(err => console.error('StopEditing error:', err));
    }
  }

  public joinPresence(projectId: any, userName: string): void {
    this.currentUserName = userName;
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke('JoinPresence', String(projectId), userName)
        .catch(err => console.error('JoinPresence error:', err));
    } else {
      console.warn('Hub not connected, cannot send JoinPresence');
    }
  }

  public leavePresence(projectId: any, userName: string): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke('LeavePresence', String(projectId), userName)
        .catch(err => console.error('LeavePresence error:', err));
    }
  }

  public onReceiveEditing(callback: (rowId: string, userName: string) => void): void {
    this.receiveEditingCb = callback;
    this.bindAllEvents();
  }

  public onUserStoppedEditing(callback: (rowId: string, userName: string) => void): void {
    this.userStoppedEditingCb = callback;
    this.bindAllEvents();
  }

  public notifyLocked(projectId: number, userName: string): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke('NotifyLocked', projectId, userName)
        .catch(err => console.error('NotifyLocked error:', err));
    }
  }

  public onLocked(callback: (userName: string) => void): void {
    this.lockedCb = callback;
    this.bindAllEvents();
  }

  public notifyUnlocked(projectId: number, userName: string) {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke('NotifyUnlocked', projectId, userName)
        .catch(err => console.error('NotifyUnlocked error:', err));
    }
  }

  public onUnlocked(callback: (userName: string) => void) {
    this.unlockedCb = callback;
    this.bindAllEvents();
  }

  public onPresenceSnapshot(callback: (users: string[]) => void): void {
    this.presenceSnapshotCb = callback;
    this.bindAllEvents();
  }

  public onUserJoined(callback: (userName: string) => void): void {
    this.userJoinedCb = callback;
    this.bindAllEvents();
  }

  public onUserLeft(callback: (userName: string) => void): void {
    this.userLeftCb = callback;
    this.bindAllEvents();
  }

  /** Tell everyone else this cell now holds an unsaved edit by this user. */
  public notifyFieldChanged(projectId: any, cellKey: string, userName: string): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke('NotifyFieldChanged', String(projectId), cellKey, userName)
        .catch(err => console.error('NotifyFieldChanged error:', err));
    }
  }

  /** Tell everyone else this cell is back to its original value. */
  public notifyFieldChangeReverted(projectId: any, cellKey: string, userName: string): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke('NotifyFieldChangeReverted', String(projectId), cellKey, userName)
        .catch(err => console.error('NotifyFieldChangeReverted error:', err));
    }
  }

  /** Drop every unsaved-edit highlight this user owns (after a save or a reset). */
  public clearFieldChanges(projectId: any, userName: string): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke('ClearFieldChanges', String(projectId), userName)
        .catch(err => console.error('ClearFieldChanges error:', err));
    }
  }

  public onFieldChanged(callback: (cellKey: string, userName: string) => void): void {
    this.fieldChangedCb = callback;
    this.bindAllEvents();
  }

  public onFieldChangeReverted(callback: (cellKey: string, userName: string) => void): void {
    this.fieldChangeRevertedCb = callback;
    this.bindAllEvents();
  }

  public onFieldChangesCleared(callback: (userName: string) => void): void {
    this.fieldChangesClearedCb = callback;
    this.bindAllEvents();
  }

  public onFieldChangesSnapshot(callback: (cellKeys: string[], userNames: string[]) => void): void {
    this.fieldChangesSnapshotCb = callback;
    this.bindAllEvents();
  }
}
